import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { StatusChip, Text } from '~/components/core';
import { Entypo } from '~/components/core/Icons';
import { Strategy } from '~/api-services/AIStrategyBuilderService/types';
import { useAppSelector, useStyles } from '~/hooks';
import { symbolsSelector } from '~/state/selectors';
import { scaleSize } from '~/constants/scale';
import { FONT_SIZE_M, FONT_SIZE_XS } from '~/constants/typography';
import { Theme } from '~/theme';

import { strategyIndicatorLine, strategyTitleLine } from '../utils/format';

type Props = {
    strategy: Strategy;
    onPress: (strategy: Strategy) => void;
    onActionsPress: (strategy: Strategy) => void;
};

/**
 * One row in the AI Strategies list: title ("Gold · Buy · 1 hour · TP 30 · SL 5"), indicator
 * subtitle, live progress while optimizing, the status badge (right, hugging the "..."), and the
 * "..." actions trigger.
 *
 * Memoized with a narrow selector: the symbols map's identity changes on every streamed quote
 * batch, so subscribing to the whole map re-renders every visible row per tick. Selecting just
 * the translated name re-renders only when that string actually changes.
 */
function StrategyIdentityRow({ strategy, onPress, onActionsPress }: Props) {
    const styles = useStyles(stylesGenerator);
    const instrumentName =
        useAppSelector((state) => symbolsSelector(state)[strategy.instrument_code]?.nameTranslated) ??
        strategy.instrument_code;

    // Title = "Gold · Buy · 1h · TP 30 · SL 5", subtitle = "EMA short | EMA long". Both read
    // `frontend_json`, which the list requests explicitly (useStrategies). Strategies created
    // before this app started writing that blob degrade to the plain market line, no subtitle.
    const title = strategyTitleLine(strategy, instrumentName);
    const subtitle = strategyIndicatorLine(strategy);

    const running = strategy.status === 'created' || strategy.status === 'inprogress';
    const percent = typeof strategy.progress_percent === 'number' ? strategy.progress_percent : null;

    return (
        <Pressable
            style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
            onPress={() => onPress(strategy)}
        >
            <View style={styles.left}>
                <Text noTranslate style={styles.title} numberOfLines={1}>
                    {title}
                </Text>
                {subtitle ? (
                    <Text noTranslate style={styles.subtitle} numberOfLines={1}>
                        {subtitle}
                    </Text>
                ) : null}
                {running ? (
                    <View style={styles.progressRow}>
                        <View style={styles.progressTrack}>
                            <View style={[styles.progressFill, { width: `${Math.min(100, percent ?? 0)}%` }]} />
                        </View>
                        {percent !== null ? (
                            <Text noTranslate style={styles.progressText}>
                                {`${Math.round(percent)}%`}
                            </Text>
                        ) : null}
                    </View>
                ) : null}
            </View>
            {/* Status badge hugging the "..." menu. StatusChip's pill sets alignSelf: 'flex-start',
                which would pin it to the top of a two-line block — the wrapper vertically centres it. */}
            <View style={styles.chipSlot}>
                <StatusChip status={strategy.status} size="sm" />
            </View>
            <Pressable
                hitSlop={10}
                onPress={() => onActionsPress(strategy)}
                style={styles.dotsButton}
                accessibilityLabel="Actions"
            >
                <Entypo name="dots-three-vertical" size={scaleSize(16)} style={styles.dotsIcon} />
            </Pressable>
        </Pressable>
    );
}

export default React.memo(StrategyIdentityRow);

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        row: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: scaleSize(12),
            paddingLeft: scaleSize(14),
            paddingRight: scaleSize(6),
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: theme.tabIconDefault,
        },
        rowPressed: {
            opacity: 0.7,
        },
        left: {
            flex: 1,
            marginRight: scaleSize(8),
        },
        title: {
            color: theme.text,
            fontSize: FONT_SIZE_M,
        },
        subtitle: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            marginTop: scaleSize(2),
        },
        progressRow: {
            flexDirection: 'row',
            alignItems: 'center',
            marginTop: scaleSize(6),
        },
        progressTrack: {
            flex: 1,
            height: scaleSize(3),
            borderRadius: scaleSize(2),
            backgroundColor: theme.backgroundSecondary,
            overflow: 'hidden',
        },
        progressFill: {
            height: '100%',
            backgroundColor: theme.button,
        },
        progressText: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            marginLeft: scaleSize(6),
        },
        // Chip-sized box hugging the "..." menu, so every status badge shares one right edge next
        // to the actions trigger (left edges run ragged as labels vary, 67-94px in English).
        chipSlot: {
            alignItems: 'flex-end',
            justifyContent: 'center',
        },
        dotsButton: {
            paddingHorizontal: scaleSize(8),
            paddingVertical: scaleSize(6),
        },
        dotsIcon: {
            color: theme.textSecondary,
        },
    });
